import { useDebounce } from 'react-use';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useControlledState } from '@/hooks';
import { CommandContent } from './components/command-content';
import { CommandEmpty } from './components/command-empty';
import { CommandInput } from './components/command-input';
import { CommandLoading } from './components/command-loading';
import { Command as CommandProvider, CommandList } from './CommandPrimitive';
import { BOX_SHADOW_STYLE, COMMAND_DELAY_DEFAULT } from './constants';
import ICommandProps from './ICommandProps';
import { CommandItem } from './types';
import { Box } from '@/components/box';
import { Select } from '../select';
import { cn } from '@/lib';
import { COMMAND_VARIANT } from './constants';
import { CommandVariant } from './types';

const Command = <T extends CommandVariant = 'simple'>({
  value,
  groups,
  loop,
  placeholder,
  maxLength,
  onValueChange,
  defaultValue,
  shouldFilter,
  openOnFocus,
  variant = 'simple' as T,
  isLoading,
  loadingLabel,
  noResultsLabel,
  showLoadingOnType,
  hideContent,
  onItemSelect,
  onDebouncedValueChange,
  delay = COMMAND_DELAY_DEFAULT,
  searchConfig,
  filter,
  footer,
  isPopover = false,
  selectionConfig,
  ref,
}: ICommandProps<T>) => {
  const internalRef = useRef<HTMLDivElement>(null);

  const availableRef = ref ?? internalRef;

  const [open, setOpen] = useState<boolean>(false);

  // Another state to indicate that the Command is `loading`. This is specifically for the `showLoadingOnType` feature
  // To indicate the time period between the user is typing and the debounce completing
  const [isPending, setIsPending] = useState<boolean>(false);

  // If the variant is `CHECKBOX`, we do not pass a value to the Input
  // Otherwise, we pass the value as is
  const passedValue = variant === COMMAND_VARIANT.CHECKBOX ? undefined : (value as string);

  // When `defaultValue` is not provided, we use empty string as fallback
  const { value: valueState, setValue: setValueState } = useControlledState<string>(passedValue, defaultValue ?? '', onValueChange);

  /**
   * Optional debounce for input value changes
   */
  useDebounce(
    () => {
      // Debounce is complete, so we turn this off
      if (showLoadingOnType) {
        setIsPending(false);
      }

      onDebouncedValueChange?.(valueState);
    },
    delay,
    [valueState, onDebouncedValueChange],
  );

  /**
   * Handle click outside to close the command dropdown
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is outside the ref element
      if (availableRef.current && !availableRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    // Add event listener to document
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [availableRef]);

  /**
   * Handler to manage input value changes.
   */
  const handleInputOnValueChange = useCallback(
    (value: string) => {
      setValueState(value);

      if (!open) {
        setOpen(true);
      }

      if (showLoadingOnType) {
        setIsPending(true);
      }
    },
    [open, setValueState, showLoadingOnType],
  );

  /**
   * Handler to manage input focus event.
   */
  const handleInputOnFocus = useCallback(() => {
    if (openOnFocus) {
      setOpen(true);
    }
  }, [openOnFocus]);

  /**
   * Handler to handle when an Item in the CommandContent is selected.
   */
  const handleContentItemOnSelect = useCallback(
    (item: CommandItem | string[]) => {
      if (variant === COMMAND_VARIANT.CHECKBOX) {
        (onItemSelect as (value: string[]) => void)?.(item as string[]);
      } else {
        // Type cast to CommandItem for other variants
        const { label, value } = item as CommandItem;

        // We set the Input Value to the label of the selected item
        setValueState(label);

        // Close the Command dropdown when an item is selected
        setOpen(false);
        (onItemSelect as (value: string) => void)?.(value as string);
      }
    },
    [onItemSelect, setValueState, variant],
  );

  /**
   * Render the command list content based on loading state and search mode
   */
  const commandContent = useMemo(() => {
    if (hideContent) {
      return null;
    }

    if (isLoading || (showLoadingOnType && isPending)) {
      return <CommandLoading loadingLabel={loadingLabel} />;
    }

    return (
      <Fragment>
        <CommandContent
          groups={groups}
          variant={variant}
          inputValue={valueState}
          checkboxValue={variant === COMMAND_VARIANT.CHECKBOX ? (value as string[]) : []}
          onItemSelect={handleContentItemOnSelect}
        />
        <CommandEmpty inputValue={valueState} noResultsLabel={noResultsLabel} />
      </Fragment>
    );
  }, [
    hideContent,
    isLoading,
    showLoadingOnType,
    isPending,
    groups,
    variant,
    valueState,
    value,
    handleContentItemOnSelect,
    noResultsLabel,
    loadingLabel,
  ]);

  const roundedClass = 'rounded-[var(--border-radius-lg)]';
  const roundedTopClass = 'rounded-t-[var(--border-radius-lg)]';

  return (
    <CommandProvider
      shouldFilter={shouldFilter}
      loop={loop}
      filter={filter}
      style={
        open
          ? {
              boxShadow: BOX_SHADOW_STYLE,
            }
          : undefined
      }
      ref={availableRef}
      className={cn('relative', open ? `rounded-none ${roundedTopClass}` : roundedClass)}
    >
      {!isPopover && (
        <CommandInput
          maxLength={maxLength}
          placeholder={placeholder}
          value={valueState}
          onValueChange={handleInputOnValueChange}
          onFocus={handleInputOnFocus}
          searchConfig={searchConfig}
          className={open ? roundedTopClass : roundedClass}
        />
      )}
      {(open || isPopover) && (
        <div
          className={cn(
            'bg-[var(--base-background)]',
            'z-1',
            !isPopover
              ? 'border border-[var(--base-border)] absolute top-[var(--spacing-10)] -left-[1px] w-[calc(100%+2px)] rounded-b-[var(--border-radius-lg)]'
              : roundedClass,
          )}
          style={{
            boxShadow: BOX_SHADOW_STYLE,
          }}
        >
          {selectionConfig?.enabled && (
            <Box className="p-[var(--spacing-2)] pb-0">
              <Select isPortal={false} options={selectionConfig.options} onChange={selectionConfig.onSelect} value={selectionConfig.value} />
            </Box>
          )}
          {isPopover && (
            <CommandInput
              maxLength={maxLength}
              placeholder={placeholder}
              value={valueState}
              onValueChange={handleInputOnValueChange}
              onFocus={handleInputOnFocus}
              searchConfig={searchConfig}
              className={cn('border-b border-b-[var(--base-border)]', hideContent ? roundedClass : roundedTopClass)}
            />
          )}
          <CommandList className="text-popover-foreground outline-none animate-in" onMouseDown={e => e.preventDefault()}>
            {commandContent}
          </CommandList>
          {footer && (
            <Box className="px-[var(--spacing-3)] py-[var(--spacing-2)] border-t border-[var(--base-border)] bg-[var(--base-muted)] rounded-b-[var(--border-radius-lg)]">
              {footer}
            </Box>
          )}
        </div>
      )}
    </CommandProvider>
  );
};

export default Command;
